import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages membership product modal forms
 * Intercepts form submissions and shows modal for membership details collection
 *
 * @typedef {object} MembershipFormRefs
 * @property {HTMLElement} membershipForm - The membership form container
 * @property {HTMLElement} progressIndicator - The progress indicator element
 * @extends Component<MembershipFormRefs>
 */
export class MembershipFormComponent extends Component {
  /** @type {string[]} */
  requiredRefs = [];

  /** @type {number} */
  #adultCount = 0;

  /** @type {number} */
  #childCount = 0;

  /** @type {Record<string, Record<string, string>>} */
  #formData = {};

  /** @type {HTMLFormElement | null} */
  #productForm = null;

  /** @type {HTMLElement | null} */
  #modal = null;

  connectedCallback() {
    super.connectedCallback();

    // Get membership requirements from data attributes
    this.#adultCount = parseInt(this.dataset.membershipAdults || '0') || 0;
    this.#childCount = parseInt(this.dataset.membershipChildren || '0') || 0;

    this.#findProductForm();
    this.#setupModal();
    this.#setupForm();
    this.#bindEvents();
  }

  /**
   * Finds the parent product form
   */
  #findProductForm() {
        // Try multiple ways to find the product form since modal may be outside the form
    this.#productForm =
      // First try: closest form (if modal is inside form)
      this.closest('form') ||
      // Second try: look for product-form-component on the page
      document.querySelector('product-form-component form') ||
      // Third try: look for form with product action
      document.querySelector('form[action*="/cart/add"]') ||
      // Fourth try: any form with an id input (variant selector)
      (() => {
        const idInput = document.querySelector('form input[name="id"]');
        return idInput ? idInput.closest('form') : null;
      })();

    this.#modal = document.getElementById('membership-modal');

    console.log('Found product form:', this.#productForm);
    console.log('Found modal:', this.#modal);
  }

  /**
   * Sets up modal event handlers
   */
  #setupModal() {
    if (!this.#modal) return;

    // Close modal handlers
    const closeButton = this.#modal.querySelector('#membership-modal-close');
    const backdrop = this.#modal.querySelector('.membership-modal__backdrop');

    if (closeButton) {
      closeButton.addEventListener('click', this.#closeModal.bind(this));
    }

    if (backdrop) {
      backdrop.addEventListener('click', this.#closeModal.bind(this));
    }

    // Continue button handler
    const continueButton = this.#modal.querySelector('#membership-continue-button');
    if (continueButton) {
      continueButton.addEventListener('click', this.#handleContinueToCart.bind(this));
    }

    // Escape key handler
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.#modal?.hasAttribute('hidden')) {
        this.#closeModal();
      }
    });
  }

  /**
   * Sets up the initial form structure
   */
  #setupForm() {
    const formContainer = this.querySelector('[ref="membershipForm"]');
    if (!formContainer) return;

    // Create accordion panels for each person
    let formHTML = '';

    // Add adult forms
    for (let i = 0; i < this.#adultCount; i++) {
      formHTML += this.#createPersonAccordion(`adult_${i}`, `Adult ${i + 1}`, 'adult');
    }

    // Add child forms
    for (let i = 0; i < this.#childCount; i++) {
      formHTML += this.#createPersonAccordion(`child_${i}`, `Child ${i + 1}`, 'child');
    }

    formContainer.innerHTML = formHTML;
    this.#updateProgress();
  }

  /**
   * Creates HTML for a person accordion panel
   * @param {string} id - Unique identifier
   * @param {string} title - Display title
   * @param {string} type - adult or child
   * @returns {string} HTML string
   */
  #createPersonAccordion(id, title, type) {
    return `
      <div class="membership-accordion-panel" data-person-id="${id}">
        <button
          type="button"
          class="membership-accordion-header"
          aria-expanded="false"
          data-accordion-trigger
        >
          <span class="membership-accordion-title">${title}</span>
          <span class="membership-accordion-status" data-status-indicator>
            <span class="status-incomplete">Incomplete</span>
            <span class="status-complete hidden">Complete</span>
          </span>
          <span class="membership-accordion-arrow">
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
              <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </button>
        <div class="membership-accordion-content" data-accordion-content>
          <div class="membership-form-fields">
            <div class="membership-field-group">
              <label for="${id}_first_name" class="membership-field-label">First Name *</label>
              <input
                type="text"
                id="${id}_first_name"
                name="properties[${id}_first_name]"
                class="membership-field-input"
                required
                data-person-field="${id}"
              >
            </div>
            <div class="membership-field-group">
              <label for="${id}_last_name" class="membership-field-label">Last Name *</label>
              <input
                type="text"
                id="${id}_last_name"
                name="properties[${id}_last_name]"
                class="membership-field-input"
                required
                data-person-field="${id}"
              >
            </div>
            <div class="membership-field-group">
              <label for="${id}_dob" class="membership-field-label">Date of Birth (DD/MM/YYYY) *</label>
              <input
                type="text"
                id="${id}_dob"
                name="properties[${id}_date_of_birth]"
                class="membership-field-input"
                required
                placeholder="DD/MM/YYYY"
                pattern="^(0[1-9]|[12][0-9]|3[01])/(0[1-9]|1[012])/[0-9]{4}$"
                data-person-field="${id}"
              >
            </div>
            ${type === 'child' ? `
            <div class="membership-field-group">
              <label for="${id}_age_group" class="membership-field-label">Age Group *</label>
              <select
                id="${id}_age_group"
                name="properties[${id}_age_group]"
                class="membership-field-input"
                required
                data-person-field="${id}"
              >
                <option value="">Select age group...</option>
                <option value="Wee Eagles">Wee Eagles</option>
                <option value="P1">P1</option>
                <option value="P2">P2</option>
                <option value="P3">P3</option>
                <option value="P4">P4</option>
                <option value="P5">P5</option>
                <option value="P6">P6</option>
                <option value="P7">P7</option>
                <option value="U13">U13</option>
                <option value="U14">U14</option>
                <option value="U15">U15</option>
                <option value="U16">U16</option>
                <option value="U18">U18</option>
                <option value="U14 Girls">U14 Girls</option>
                <option value="U16 Girls">U16 Girls</option>
                <option value="U18 Girls">U18 Girls</option>
              </select>
            </div>
            ` : ''}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Binds event listeners
   */
  #bindEvents() {
    // Accordion toggles - handle both click and touch events for mobile compatibility
    const handleAccordionToggle = (event) => {
      // Try multiple ways to find the accordion trigger for better mobile compatibility
      let trigger = null;

      // First try the modern closest method
      if (event.target && typeof event.target.closest === 'function') {
        trigger = event.target.closest('[data-accordion-trigger]');
      }

      // Fallback: traverse up the DOM manually
      if (!trigger) {
        let element = event.target;
        while (element && element !== this) {
          if (element.hasAttribute && element.hasAttribute('data-accordion-trigger')) {
            trigger = element;
            break;
          }
          element = element.parentElement;
        }
      }

      if (trigger) {
        event.preventDefault(); // Prevent any default behavior
        event.stopPropagation(); // Stop event bubbling
        this.#toggleAccordion(trigger);
      }
    };

    // Add both click and touchstart events for better mobile support
    this.addEventListener('click', handleAccordionToggle);
    this.addEventListener('touchstart', handleAccordionToggle, { passive: false });

    // Form field validation
    this.addEventListener('input', (event) => {
      const target = event.target;
      if (target && target.hasAttribute && target.hasAttribute('data-person-field')) {
        this.#handleFieldInput(target);
      }
    });

    // Handle select field changes
    this.addEventListener('change', (event) => {
      const target = event.target;
      if (target && target.hasAttribute && target.hasAttribute('data-person-field') && target.tagName.toLowerCase() === 'select') {
        this.#handleFieldInput(target);
      }
    });

    // Date format validation
    this.addEventListener('input', (event) => {
      const target = event.target;
      if (target && target.id && target.id.includes('_dob')) {
        this.#formatDateInput(target);
      }
    });
  }

  /**
   * Toggles accordion panel
   * @param {HTMLElement} trigger
   */
  #toggleAccordion(trigger) {
    if (!trigger) return;

    console.log('Toggling accordion for:', trigger); // Debug log

    const content = trigger.nextElementSibling;
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    const newExpandedState = !isExpanded;

    // Update aria-expanded attribute
    trigger.setAttribute('aria-expanded', newExpandedState.toString());

    // Show/hide content
    if (content) {
      if (newExpandedState) {
        content.style.display = 'block';
        content.style.maxHeight = content.scrollHeight + 'px'; // For smooth animation
      } else {
        content.style.display = 'none';
        content.style.maxHeight = '0';
      }
    }

    // Update accordion panel state
    let panel = null;

    // Try modern closest method first
    if (typeof trigger.closest === 'function') {
      panel = trigger.closest('.membership-accordion-panel');
    } else {
      // Fallback: traverse up manually
      let element = trigger.parentElement;
      while (element) {
        if (element.classList && element.classList.contains('membership-accordion-panel')) {
          panel = element;
          break;
        }
        element = element.parentElement;
      }
    }

    if (panel) {
      if (newExpandedState) {
        panel.classList.add('expanded');
      } else {
        panel.classList.remove('expanded');
      }
    }

    console.log('Accordion toggled. New state:', newExpandedState); // Debug log
  }

  /**
   * Handles input field changes
   * @param {HTMLInputElement} field
   */
  #handleFieldInput(field) {
    const personId = field.dataset.personField;
    if (!personId) return;

    // Initialize person data if not exists
    if (!this.#formData[personId]) {
      this.#formData[personId] = {};
    }

    // Update form data
    const fieldParts = field.id.split('_');
    let fieldType = fieldParts[fieldParts.length - 1]; // Gets last part like 'name', 'dob', etc.

    // Handle compound field names like 'age_group'
    if (fieldParts.length > 2 && fieldParts[fieldParts.length - 2] === 'age') {
      fieldType = 'age_group';
    }

    this.#formData[personId][fieldType] = field.value;

    // Check if this person's form is complete
    this.#checkPersonCompletion(personId);
    this.#updateProgress();
  }

  /**
   * Formats date input as user types
   * @param {HTMLInputElement} input
   */
  #formatDateInput(input) {
    let value = input.value.replace(/\D/g, ''); // Remove non-digits

    if (value.length >= 2) {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
    if (value.length >= 5) {
      value = value.substring(0, 5) + '/' + value.substring(5, 9);
    }

    input.value = value;
  }

  /**
   * Checks if a person's form is complete
   * @param {string} personId
   */
  #checkPersonCompletion(personId) {
    const panel = this.querySelector(`[data-person-id="${personId}"]`);
    if (!panel) return;

    const inputs = panel.querySelectorAll('[data-person-field]');
    const isComplete = Array.from(inputs).every(input => {
      // For select elements, check if a non-empty option is selected
      if (input.tagName.toLowerCase() === 'select') {
        return input.value.trim() !== '';
      }
      // For other inputs, check if they have a value
      return input.value.trim() !== '';
    });

    const statusIndicator = panel.querySelector('[data-status-indicator]');
    const incompleteSpan = statusIndicator?.querySelector('.status-incomplete');
    const completeSpan = statusIndicator?.querySelector('.status-complete');

    if (isComplete && incompleteSpan && completeSpan) {
      incompleteSpan.classList.add('hidden');
      completeSpan.classList.remove('hidden');
      panel.classList.add('complete');
    } else if (incompleteSpan && completeSpan) {
      incompleteSpan.classList.remove('hidden');
      completeSpan.classList.add('hidden');
      panel.classList.remove('complete');
    }
  }

  /**
   * Updates the progress indicator
   */
  #updateProgress() {
    // Look for progress indicator in modal if modal has been moved
    const progressIndicator = this.#modal?.querySelector('[ref="progressIndicator"]') || this.querySelector('[ref="progressIndicator"]');
    if (!progressIndicator) return;

    const totalPersons = this.#adultCount + this.#childCount;

    // Look for complete panels in modal if modal has been moved
    const completePanels = this.#modal?.querySelectorAll('.membership-accordion-panel.complete').length ||
                          this.querySelectorAll('.membership-accordion-panel.complete').length;

    console.log('Progress update - Total persons:', totalPersons, 'Complete panels:', completePanels);

    // Show progress with indication that at least 1 is required
    if (completePanels === 0) {
      progressIndicator.textContent = `${completePanels} of ${totalPersons} completed (at least 1 required)`;
    } else if (completePanels === totalPersons) {
      progressIndicator.textContent = `${completePanels} of ${totalPersons} completed`;
    } else {
      progressIndicator.textContent = `${completePanels} of ${totalPersons} completed (ready to continue)`;
    }

    // Update continue button state
    this.#updateContinueButton();
  }

  /**
   * Updates continue button state
   */
  #updateContinueButton() {
    const continueButton = this.#modal?.querySelector('#membership-continue-button');
    if (!continueButton) return;

    // Look for complete panels in the modal instead of 'this' since modal may have been moved
    const completePanels = this.#modal.querySelectorAll('.membership-accordion-panel.complete').length;

    console.log('Updating continue button. Complete panels:', completePanels);

    // Allow submission as long as at least 1 person has completed their details
    const isFormValid = completePanels >= 1;

    if (isFormValid) {
      console.log('Form is valid - enabling continue button');
      continueButton.removeAttribute('disabled');
      continueButton.classList.remove('membership-form-incomplete');
    } else {
      console.log('Form is not valid - disabling continue button');
      continueButton.setAttribute('disabled', 'true');
      continueButton.classList.add('membership-form-incomplete');
    }
  }

  /**
   * Opens the membership modal
   */
  openModal() {
    if (this.#modal) {
      // Move modal to end of body to ensure proper DOM positioning and z-index layering
      if (this.#modal.parentNode !== document.body) {
        console.log('Moving modal to body and rebinding events');
        document.body.appendChild(this.#modal);

        // Rebind events after moving modal since event delegation may be broken
        this.#rebindModalEvents();
      }

      this.#modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';

      // Auto-expand if there's only one accordion panel
      this.#autoExpandSingleAccordion();

      // Focus first input after animation
      setTimeout(() => {
        const firstInput = this.#modal.querySelector('.membership-field-input');
        if (firstInput) {
          firstInput.focus();
        }
      }, 300);
    }
  }

  /**
   * Closes the membership modal
   */
  #closeModal() {
    if (this.#modal) {
      this.#modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
    }
  }

  /**
   * Rebinds events after modal is moved to body
   */
  #rebindModalEvents() {
    if (!this.#modal) return;

    console.log('Rebinding modal events');

    // Remove existing event listeners by cloning and replacing the modal
    // This ensures we don't have duplicate event listeners
    const newModal = this.#modal.cloneNode(true);
    this.#modal.parentNode.replaceChild(newModal, this.#modal);
    this.#modal = newModal;

    // Rebind accordion events directly to the modal
    this.#modal.addEventListener('click', (event) => {
      console.log('Modal click event:', event.target);
      const trigger = this.#findAccordionTrigger(event.target);
      if (trigger) {
        console.log('Found accordion trigger:', trigger);
        event.preventDefault();
        event.stopPropagation();
        this.#toggleAccordion(trigger);
      }
    });

    // Also bind touch events for mobile
    this.#modal.addEventListener('touchstart', (event) => {
      console.log('Modal touch event:', event.target);
      const trigger = this.#findAccordionTrigger(event.target);
      if (trigger) {
        console.log('Found accordion trigger via touch:', trigger);
        event.preventDefault();
        event.stopPropagation();
        this.#toggleAccordion(trigger);
      }
    }, { passive: false });

    // Rebind form field events
    this.#modal.addEventListener('input', (event) => {
      const target = event.target;
      if (target && target.hasAttribute && target.hasAttribute('data-person-field')) {
        this.#handleFieldInput(target);
      }
    });

    this.#modal.addEventListener('change', (event) => {
      const target = event.target;
      if (target && target.hasAttribute && target.hasAttribute('data-person-field') && target.tagName.toLowerCase() === 'select') {
        this.#handleFieldInput(target);
      }
    });

    // Rebind close button
    const closeButton = this.#modal.querySelector('.membership-modal__close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.#closeModal();
      });
    }

    // Rebind continue button
    const continueButton = this.#modal.querySelector('#membership-continue-button');
    if (continueButton) {
      continueButton.addEventListener('click', this.#handleContinueToCart.bind(this));
    }

    // Force update continue button state after rebinding
    setTimeout(() => {
      this.#updateContinueButton();
    }, 100);
  }

  /**
   * Helper method to find accordion trigger from event target
   */
  #findAccordionTrigger(target) {
    if (!target) return null;

    // First try the modern closest method
    if (typeof target.closest === 'function') {
      const trigger = target.closest('[data-accordion-trigger]');
      if (trigger) return trigger;
    }

    // Fallback: traverse up the DOM manually
    let element = target;
    while (element && element !== this.#modal) {
      if (element.hasAttribute && element.hasAttribute('data-accordion-trigger')) {
        return element;
      }
      element = element.parentElement;
    }

    return null;
  }

  /**
   * Auto-expands accordion if there's only one panel
   */
  #autoExpandSingleAccordion() {
    if (!this.#modal) return;

    const accordionTriggers = this.#modal.querySelectorAll('[data-accordion-trigger]');
    console.log('Found accordion triggers:', accordionTriggers.length);

    if (accordionTriggers.length === 1) {
      console.log('Auto-expanding single accordion panel');
      const trigger = accordionTriggers[0];
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

      if (!isExpanded) {
        this.#toggleAccordion(trigger);
      }
    }
  }

  /**
   * Handles continue to cart button click
   */
  #handleContinueToCart() {
    console.log('Continue button clicked');
    console.log('Form validation:', this.validateForm());
    console.log('Product form:', this.#productForm);

    if (!this.validateForm()) {
      console.error('Form validation failed');
      return;
    }

    if (!this.#productForm) {
      console.error('No product form found');
      return;
    }

    console.log('Adding form data to product form');
    // Add all form data as hidden inputs to the product form
    this.#addFormDataToProductForm();

    console.log('Closing modal');
    // Close modal
    this.#closeModal();

    console.log('Submitting product form');
    // Submit the product form
    this.#submitProductForm();
  }

  /**
   * Adds form data as hidden inputs to product form
   */
  #addFormDataToProductForm() {
    if (!this.#productForm) return;

    // Remove any existing membership hidden inputs
    const existingInputs = this.#productForm.querySelectorAll('.membership-hidden-input');
    existingInputs.forEach(input => input.remove());

    // Group form data by person and format as requested
    const groupedData = {};
    const allInputs = this.querySelectorAll('[data-person-field]');

    allInputs.forEach(input => {
      if (input.value.trim()) {
        const personId = input.dataset.personField;

        // Extract field type properly from IDs like "adult_0_first_name", "adult_0_last_name", "adult_0_dob", "child_0_age_group"
        const idParts = input.id.split('_');
        const fieldType = idParts.slice(2).join('_'); // Get everything after "adult_0" or "child_0"

        if (!groupedData[personId]) {
          groupedData[personId] = {};
        }

        // Map field types to proper title case readable names
        let readableField = fieldType;
        if (fieldType === 'first_name') readableField = 'First Name';
        else if (fieldType === 'last_name') readableField = 'Last Name';
        else if (fieldType === 'dob') readableField = 'Date of Birth';
        else if (fieldType === 'age_group') readableField = 'Age Group';

        groupedData[personId][readableField] = input.value.trim();
      }
    });

    // Create formatted line item properties
    Object.keys(groupedData).forEach(personId => {
      const personData = groupedData[personId];

      // Create readable person label
      let personLabel = '';
      if (personId.startsWith('adult_')) {
        const adultNum = parseInt(personId.split('_')[1]) + 1;
        personLabel = `adult ${adultNum}`;
      } else if (personId.startsWith('child_')) {
        const childNum = parseInt(personId.split('_')[1]) + 1;
        personLabel = `child ${childNum}`;
      }

      // Format as requested: "adult 1: {first name: John, last name: Smith, date of birth: 15/03/1985}"
      const formattedData = Object.keys(personData)
        .map(key => `${key}: ${personData[key]}`)
        .join(', ');

      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = `properties[${personLabel}]`;
      hiddenInput.value = `{${formattedData}}`;
      hiddenInput.classList.add('membership-hidden-input');
      this.#productForm.appendChild(hiddenInput);
    });
  }

      /**
   * Submits the product form using Cart AJAX API
   */
  #submitProductForm() {
    console.log('Submitting product form via Cart AJAX API');

    // Use stored form reference from MembershipFormManager
    const productForm = this._productForm || this.#productForm;

    if (!productForm) {
      console.error('No product form found');
      return;
    }

    console.log('Using product form:', productForm);

    // Get form data
    const formData = new FormData(productForm);

    // Create the request payload for Cart AJAX API
    const cartData = {
      items: [{
        id: formData.get('id'), // variant ID
        quantity: parseInt(formData.get('quantity')) || 1,
        properties: {}
      }]
    };

    // Add all form properties (including membership data and gift status)
    for (let [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        // Extract the property name from properties[name] format
        const propertyName = key.replace('properties[', '').replace(']', '');
        cartData.items[0].properties[propertyName] = value;
      }
    }

    console.log('Cart data to submit:', cartData);

    // Submit to Cart AJAX API
    fetch(window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(cartData)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      console.log('Cart add success:', data);

      // Trigger add-to-cart button animation
      this.#triggerAddToCartAnimation();

      // Dispatch the proper CartAddEvent exactly like the original theme does
      this.#dispatchCartAddEvent(data, formData);

      // Update cart count as backup
      this.#updateCartCount(data);
    })
    .catch(error => {
      console.error('Cart add error:', error);
      // You could show an error message to the user here
    });
  }

  /**
   * Triggers the add-to-cart button animation
   */
  #triggerAddToCartAnimation() {
    // Find the add-to-cart button and trigger its animation
    const addToCartComponent = this._productFormComponent?.querySelector('add-to-cart-component');
    if (addToCartComponent && addToCartComponent.animateAddToCart) {
      addToCartComponent.animateAddToCart();
    }
  }

    /**
   * Dispatches CartAddEvent exactly like the original theme
   */
  #dispatchCartAddEvent(cartData, formData) {
    // Create CartAddEvent exactly like the original ProductFormComponent does
    const event = new CartAddEvent({}, formData.get('id'), {
      source: 'membership-form-component',
      itemCount: parseInt(formData.get('quantity')) || 1,
      productId: this._productFormComponent?.dataset?.productId,
      didError: false,
      sections: cartData.sections || {}
    });

    console.log('Dispatching CartAddEvent:', event);

    // Dispatch from the product form component if available, otherwise from document
    if (this._productFormComponent) {
      this._productFormComponent.dispatchEvent(event);
    } else {
      document.dispatchEvent(event);
    }
  }

  /**
   * Updates cart count in UI (backup method)
   */
  #updateCartCount(cartData) {
    // Look for cart count elements to update
    const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .cart-bubble-count');

    if (cartData.items && cartData.items.length > 0) {
      // Calculate total quantity
      const totalQuantity = cartData.items.reduce((total, item) => total + item.quantity, 0);

      cartCountElements.forEach(element => {
        element.textContent = totalQuantity.toString();
      });

      // Show cart count if it was hidden
      cartCountElements.forEach(element => {
        element.style.display = totalQuantity > 0 ? '' : 'none';
      });
    }
  }

  /**
   * Validates the entire form
   * @returns {boolean}
   */
  validateForm() {
    const completePanels = this.querySelectorAll('.membership-accordion-panel.complete').length;

    // Allow submission as long as at least 1 person has completed their details
    return completePanels >= 1;
  }
}

/**
 * Global membership form manager
 * Handles form interception and modal triggering
 */
class MembershipFormManager {
  constructor() {
    this.init();
  }

  init() {
    // Find all product forms and add event listeners
    const productForms = document.querySelectorAll('product-form-component');
    productForms.forEach(form => {
      this.#setupFormInterception(form);
    });

    // Also check for dynamically loaded forms
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.matches && node.matches('product-form-component')) {
            this.#setupFormInterception(node);
          }
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Sets up form submission interception for membership and camp products
   * @param {HTMLElement} productFormComponent
   */
  #setupFormInterception(productFormComponent) {
    const form = productFormComponent.querySelector('form');

    // Check for membership products
    const membershipComponent = document.querySelector('membership-form-component');

    // Check for camp products
    const campComponent = document.querySelector('camp-form-component');

    // Handle membership products
    if (form && membershipComponent) {
      // Store references for later use
      membershipComponent._productFormComponent = productFormComponent;
      membershipComponent._productForm = form;

      console.log('Setting up membership form interception');
      this.#setupMembershipInterception(productFormComponent, form, membershipComponent);
    }

    // Handle camp products
    if (form && campComponent) {
      // Store references for later use
      campComponent._productFormComponent = productFormComponent;
      campComponent._productForm = form;

      console.log('Setting up camp form interception');
      this.#setupCampInterception(productFormComponent, form, campComponent);
    }
  }

  /**
   * Sets up form interception specifically for membership products
   */
  #setupMembershipInterception(productFormComponent, form, membershipComponent) {

    // Override the ProductFormComponent's handleSubmit method if it exists
    const originalHandleSubmit = productFormComponent.handleSubmit;
    if (originalHandleSubmit) {
      productFormComponent.handleSubmit = function(event) {
        console.log('ProductForm handleSubmit intercepted - showing membership modal');

        // Always prevent submission and show modal for membership products
        event.preventDefault();
        event.stopPropagation();
        membershipComponent.openModal();
      };
    }

    // ALSO intercept form submission events as backup
    form.addEventListener('submit', (event) => {
      console.log('Form submit event intercepted - showing membership modal');

      // Always prevent submission and show modal for membership products
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      membershipComponent.openModal();
    }, true); // Use capture phase to ensure we get the event first

    // ALSO intercept button clicks as a third layer of protection
    const submitButtons = form.querySelectorAll('button[type="submit"], input[type="submit"]');
    submitButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        console.log('Submit button click intercepted - showing membership modal');

        // Always prevent click and show modal for membership products
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        membershipComponent.openModal();
      }, true); // Use capture phase
    });
  }

  /**
   * Sets up form interception specifically for camp products
   */
  #setupCampInterception(productFormComponent, form, campComponent) {
    // Flag to track if camp form has been completed
    let campFormCompleted = false;

    // Listen for camp form completion
    campComponent.addEventListener('campFormCompleted', () => {
      campFormCompleted = true;
    });

    // Override the ProductFormComponent's handleSubmit method if it exists
    const originalHandleSubmit = productFormComponent.handleSubmit;
    if (originalHandleSubmit) {
      productFormComponent.handleSubmit = function(event) {
        console.log('ProductForm handleSubmit intercepted for camp', {
          campFormCompleted
        });

        // If camp form completed, allow normal submission
        if (campFormCompleted) {
          console.log('Camp form completed - calling original handleSubmit');
          return originalHandleSubmit.call(this, event);
        }

        // If camp form not completed, prevent submission and show modal
        console.log('Camp form not completed - preventing submission and showing camp modal');
        event.preventDefault();
        event.stopPropagation();
        campComponent.openModal();
      };
    }

    // ALSO intercept form submission events as backup
    form.addEventListener('submit', (event) => {
      console.log('Form submit event intercepted for camp', {
        campFormCompleted
      });

      // If camp form completed, allow normal submission
      if (campFormCompleted) {
        console.log('Camp form completed - allowing normal form submission');
        return; // Let normal submission proceed
      }

      // If camp form not completed, prevent submission and show modal
      console.log('Camp form not completed - preventing form submission and showing camp modal');
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      campComponent.openModal();
    }, true); // Use capture phase to ensure we get the event first

    // ALSO intercept button clicks as a third layer of protection
    const submitButtons = form.querySelectorAll('button[type="submit"], input[type="submit"]');
    submitButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        console.log('Submit button click intercepted for camp', {
          campFormCompleted
        });

        // If camp form completed, allow normal click
        if (campFormCompleted) {
          console.log('Camp form completed - allowing normal button click');
          return; // Let normal click proceed
        }

        // If camp form not completed, prevent click and show modal
        console.log('Camp form not completed - preventing button click and showing camp modal');
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        campComponent.openModal();
      }, true); // Use capture phase
    });
  }
}

/**
 * A custom element that manages camp product modal forms
 * Intercepts form submissions and shows modal for camp details collection
 *
 * @typedef {object} CampFormRefs
 * @property {HTMLElement} campForm - The camp form container
 * @property {HTMLElement} progressIndicator - The progress indicator element
 * @extends Component<CampFormRefs>
 */
export class CampFormComponent extends Component {
  /** @type {string[]} */
  requiredRefs = [];

  /** @type {Record<string, string>} */
  #formData = {};

  /** @type {HTMLFormElement | null} */
  #productForm = null;

  /** @type {HTMLElement | null} */
  #modal = null;

  connectedCallback() {
    super.connectedCallback();

    this.#findProductForm();
    this.#setupModal();
    this.#setupForm();
    this.#bindEvents();
  }

  /**
   * Finds the parent product form
   */
  #findProductForm() {
    // Try multiple ways to find the product form since modal may be outside the form
    this.#productForm =
      // First try: closest form (if modal is inside form)
      this.closest('form') ||
      // Second try: look for product-form-component on the page
      document.querySelector('product-form-component form') ||
      // Third try: look for form with product action
      document.querySelector('form[action*="/cart/add"]') ||
      // Fourth try: any form with an id input (variant selector)
      (() => {
        const idInput = document.querySelector('form input[name="id"]');
        return idInput ? idInput.closest('form') : null;
      })();

    this.#modal = document.getElementById('camp-modal');

    console.log('Found product form for camp:', this.#productForm);
    console.log('Found camp modal:', this.#modal);
  }

  /**
   * Sets up modal event handlers
   */
  #setupModal() {
    if (!this.#modal) return;

    // Close modal handlers
    const closeButton = this.#modal.querySelector('#camp-modal-close');
    const backdrop = this.#modal.querySelector('.membership-modal__backdrop');

    if (closeButton) {
      closeButton.addEventListener('click', this.#closeModal.bind(this));
    }

    if (backdrop) {
      backdrop.addEventListener('click', this.#closeModal.bind(this));
    }

    // Continue button handler
    const continueButton = this.#modal.querySelector('#camp-continue-button');
    if (continueButton) {
      continueButton.addEventListener('click', this.#handleContinueToCart.bind(this));
    }

    // Escape key handler
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.#modal?.hasAttribute('hidden')) {
        this.#closeModal();
      }
    });
  }

  /**
   * Sets up the initial form structure
   */
  #setupForm() {
    const formContainer = this.querySelector('[ref="campForm"]');
    if (!formContainer) return;

    // Create single form for camp registration
    const formHTML = this.#createCampForm();
    formContainer.innerHTML = formHTML;
    this.#updateProgress();
  }

  /**
   * Creates HTML for camp registration form
   * @returns {string} HTML string
   */
  #createCampForm() {
    return `
      <div class="membership-accordion-panel expanded" data-camp-form>
        <div class="membership-accordion-content" style="display: block;">
          <div class="membership-form-fields">
            <div class="membership-field-group">
              <label for="camp_player_name" class="membership-field-label">Player Name *</label>
              <input
                type="text"
                id="camp_player_name"
                name="properties[Player Name]"
                class="membership-field-input"
                required
                data-camp-field="player_name"
              >
            </div>
            <div class="membership-field-group">
              <label for="camp_emergency_contact_name" class="membership-field-label">Emergency Contact Name *</label>
              <input
                type="text"
                id="camp_emergency_contact_name"
                name="properties[Emergency Contact Name]"
                class="membership-field-input"
                required
                data-camp-field="emergency_contact_name"
              >
            </div>
            <div class="membership-field-group">
              <label for="camp_emergency_contact_number" class="membership-field-label">Emergency Contact Number *</label>
              <input
                type="tel"
                id="camp_emergency_contact_number"
                name="properties[Emergency Contact Number]"
                class="membership-field-input"
                required
                data-camp-field="emergency_contact_number"
              >
            </div>
            <div class="membership-field-group">
              <label for="camp_medical_conditions" class="membership-field-label">Any Medical Conditions / Allergies? *</label>
              <select
                id="camp_medical_conditions"
                name="properties[Medical Conditions/Allergies]"
                class="membership-field-input"
                required
                data-camp-field="medical_conditions"
              >
                <option value="">Please select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div class="membership-field-group">
              <label for="camp_photo_permission" class="membership-field-label">Permission to take photos? *</label>
              <select
                id="camp_photo_permission"
                name="properties[Photo Permission]"
                class="membership-field-input"
                required
                data-camp-field="photo_permission"
              >
                <option value="">Please select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Binds event listeners
   */
  #bindEvents() {
    // Form field validation
    this.addEventListener('input', (event) => {
      const target = event.target;
      if (target && target.hasAttribute && target.hasAttribute('data-camp-field')) {
        this.#handleFieldInput(target);
      }
    });

    // Handle select field changes
    this.addEventListener('change', (event) => {
      const target = event.target;
      if (target && target.hasAttribute && target.hasAttribute('data-camp-field') && target.tagName.toLowerCase() === 'select') {
        this.#handleFieldInput(target);
      }
    });
  }

  /**
   * Handles field input and updates form data
   * @param {HTMLInputElement|HTMLSelectElement} field
   */
  #handleFieldInput(field) {
    const fieldName = field.getAttribute('data-camp-field');
    const value = field.value.trim();

    if (fieldName) {
      this.#formData[fieldName] = value;
      this.#updateProgress();
    }
  }

  /**
   * Updates progress indicator
   */
  #updateProgress() {
    // Look for progress indicator in modal if modal has been moved
    const progressIndicator = this.#modal?.querySelector('[ref="progressIndicator"]') || this.querySelector('[ref="progressIndicator"]');
    const continueButton = this.#modal?.querySelector('#camp-continue-button');

    if (!progressIndicator || !continueButton) return;

    const isComplete = this.validateForm();

    console.log('Camp progress update - Form complete:', isComplete);

    if (isComplete) {
      progressIndicator.textContent = 'Registration details complete';
      continueButton.disabled = false;
      continueButton.classList.remove('membership-form-incomplete');
      console.log('Camp form complete - enabling continue button');
    } else {
      progressIndicator.textContent = 'Registration details required';
      continueButton.disabled = true;
      continueButton.classList.add('membership-form-incomplete');
      console.log('Camp form incomplete - disabling continue button');
    }
  }

  /**
   * Validates the entire form
   * @returns {boolean}
   */
  validateForm() {
    const requiredFields = ['player_name', 'emergency_contact_name', 'emergency_contact_number', 'medical_conditions', 'photo_permission'];

    return requiredFields.every(fieldName => {
      const value = this.#formData[fieldName];
      return value && value.trim().length > 0;
    });
  }

  /**
   * Opens the modal
   */
  openModal() {
    if (!this.#modal) return;

    console.log('Opening camp modal');

    // Move modal to end of body to ensure proper DOM positioning and z-index layering
    if (this.#modal.parentNode !== document.body) {
      console.log('Moving camp modal to body and rebinding events');
      document.body.appendChild(this.#modal);

      // Rebind events after moving modal
      this.#rebindCampModalEvents();
    }

    this.#modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
  }

  /**
   * Closes the modal
   */
  #closeModal() {
    if (!this.#modal) return;

    console.log('Closing camp modal');
    this.#modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
  }

  /**
   * Rebinds events after modal is moved to body
   */
  #rebindCampModalEvents() {
    if (!this.#modal) return;

    console.log('Rebinding camp modal events');

    // Remove existing event listeners by cloning and replacing the modal
    const newModal = this.#modal.cloneNode(true);
    this.#modal.parentNode.replaceChild(newModal, this.#modal);
    this.#modal = newModal;

    // Rebind form field events
    this.#modal.addEventListener('input', (event) => {
      const target = event.target;
      if (target && target.hasAttribute && target.hasAttribute('data-camp-field')) {
        this.#handleFieldInput(target);
      }
    });

    this.#modal.addEventListener('change', (event) => {
      const target = event.target;
      if (target && target.hasAttribute && target.hasAttribute('data-camp-field') && target.tagName.toLowerCase() === 'select') {
        this.#handleFieldInput(target);
      }
    });

    // Rebind close button
    const closeButton = this.#modal.querySelector('.membership-modal__close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.#closeModal();
      });
    }

    // Rebind continue button for camp
    const continueButton = this.#modal.querySelector('#camp-continue-button');
    if (continueButton) {
      continueButton.addEventListener('click', this.#handleContinueToCart.bind(this));
    }

    // Force update continue button state after rebinding
    setTimeout(() => {
      this.#updateProgress();
    }, 100);
  }

  /**
   * Handles continue to cart button click
   */
  #handleContinueToCart() {
    console.log('Camp continue button clicked');
    console.log('Form validation:', this.validateForm());
    console.log('Product form:', this.#productForm);

    if (!this.validateForm()) {
      console.error('Camp form validation failed');
      return;
    }

    if (!this.#productForm) {
      console.error('No product form found for camp');
      return;
    }

    console.log('Adding camp form data to product form');
    // Add all form data as hidden inputs to the product form
    this.#addFormDataToProductForm();

    console.log('Dispatching camp form completed event');
    // Dispatch event to signal camp form completion
    this.dispatchEvent(new CustomEvent('campFormCompleted', {
      bubbles: true,
      detail: { formData: this.#formData }
    }));

    console.log('Closing camp modal');
    // Close modal
    this.#closeModal();

    console.log('Submitting product form');
    // Submit the product form
    this.#submitProductForm();
  }

  /**
   * Adds form data as hidden inputs to product form
   */
  #addFormDataToProductForm() {
    if (!this.#productForm) return;

    // Remove any existing camp form data inputs
    const existingInputs = this.#productForm.querySelectorAll('input[data-camp-form-data]');
    existingInputs.forEach(input => input.remove());

    // Add new inputs for each form field
    const formFields = this.querySelectorAll('[data-camp-field]');
    formFields.forEach(field => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = field.name;
      input.value = field.value;
      input.setAttribute('data-camp-form-data', 'true');
      this.#productForm.appendChild(input);
    });
  }

  /**
   * Submits the product form
   */
  #submitProductForm() {
  const form = this.#productForm;
  if (!form) return;

  const variantId = form.querySelector('[name="id"]')?.value;
  const quantity = Number(
    form.querySelector('[name="quantity"]')?.value || 1
  );

  const properties = {
    "Player Name": this.#formData.player_name || "",
    "Emergency Contact Name": this.#formData.emergency_contact_name || "",
    "Emergency Contact Number": this.#formData.emergency_contact_number || "",
    "Medical Conditions/Allergies": this.#formData.medical_conditions || "",
    "Photo Permission": this.#formData.photo_permission || ""
  };

  fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json"
    },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: quantity,
          properties: properties
        }
      ]
    })
  })
    .then(response => {
      if (!response.ok) {
        throw new Error("The camp ticket could not be added to the cart.");
      }

      return response.json();
    })
    .then(() => {
      window.location.href =
        `${window.Shopify?.routes?.root || "/"}cart`;
    })
    .catch(error => {
      console.error("Camp cart error:", error);
    });
}

}

// Register the custom elements
if (!customElements.get('membership-form-component')) {
  customElements.define('membership-form-component', MembershipFormComponent);
}

if (!customElements.get('camp-form-component')) {
  customElements.define('camp-form-component', CampFormComponent);
}

// Initialize the membership form manager when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new MembershipFormManager();
});

// Also initialize if this script loads after DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MembershipFormManager();
  });
} else {
  new MembershipFormManager();
}